import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Progress,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const Skills: React.FC = () => {
  const bg = useColorModeValue("gray.50", "gray.900");
  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const bodyTextColor = useColorModeValue("gray.700", "gray.300");
  const progressBg = useColorModeValue("gray.200", "gray.600");

  const skills = [
    { name: "React JS", level: 85, category: "Frontend" },
    { name: "Next.js", level: 80, category: "Framework" },
    { name: "TypeScript", level: 75, category: "Language" },
    { name: "React Native", level: 70, category: "Mobile" },
    { name: "TailwindCSS", level: 95, category: "Styling" },
    { name: "Chakra UI", level: 95, category: "UI Library" },
    { name: "Redux", level: 80, category: "State Management" },
    { name: "Git & GitHub", level: 70, category: "Tools" },
  ];

  const certifications = [
    "Designing Organizational Structure - Harvard Business School (Online)",
    "Resilient Leadership - Harvard Business School (Online)",
    "Prompt Engineering - freeCodeCamp.org",
    "Nanodegree in Full Stack Development - Udacity",
    "Full Stack Web Development - SideHustle",
  ];

  const categoryColors: Record<string, string> = {
    Frontend: "blue",
    Framework: "purple",
    Language: "green",
    Mobile: "orange",
    Styling: "pink",
    "UI Library": "cyan",
    "State Management": "red",
    Tools: "teal",
  };

  return (
    <Box id="skills" py={20} bg={bg}>
      <Container maxW="7xl">
        <MotionBox
          textAlign="center"
          mb={16}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Heading
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="bold"
            color={headingColor}
            mb={4}
          >
            Skills & Technologies
          </Heading>
          <Text fontSize="xl" color={textColor} maxW="3xl" mx="auto">
            A comprehensive overview of my technical skills and the
            certifications I've earned to stay current with industry best
            practices.
          </Text>
        </MotionBox>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12}>
          <MotionBox
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Heading size="lg" mb={8} color={headingColor}>
              Core Skills
            </Heading>
            <VStack spacing={6} align="stretch">
              {skills.map((skill, index) => (
                <MotionBox
                  key={skill.name}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <HStack justify="space-between" mb={2}>
                    <HStack>
                      <Text fontWeight="semibold" color={headingColor}>
                        {skill.name}
                      </Text>
                      <Badge
                        colorScheme={categoryColors[skill.category]}
                        size="sm"
                      >
                        {skill.category}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" color={textColor}>
                      {skill.level}%
                    </Text>
                  </HStack>
                  <Progress
                    value={skill.level}
                    colorScheme="brand"
                    bg={progressBg}
                    borderRadius="full"
                    size="md"
                  />
                </MotionBox>
              ))}
            </VStack>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Heading size="lg" mb={8} color={headingColor}>
              Education & Certifications
            </Heading>
            <VStack align="start" spacing={6}>
              <Box>
                <Heading size="md" mb={4} color={headingColor}>
                  Education
                </Heading>
                <VStack align="start" spacing={3}>
                  <Box>
                    <Text fontWeight="semibold" color={headingColor}>
                      BEd. in Mathematics
                    </Text>
                    <Text color={textColor} fontSize="sm">
                      University of Ibadan (2018 - 2022)
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color={headingColor}>
                      National Diploma in Computer Science
                    </Text>
                    <Text color={textColor} fontSize="sm">
                      The Polytechnic, Ibadan (2015 - 2017)
                    </Text>
                  </Box>
                </VStack>
              </Box>

              <Box>
                <Heading size="md" mb={4} color={headingColor}>
                  Recent Certifications
                </Heading>
                <VStack align="start" spacing={3}>
                  {certifications.map((cert, index) => (
                    <MotionBox
                      key={cert}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <HStack>
                        <Box w={2} h={2} bg="brand.500" borderRadius="full" />
                        <Text color={bodyTextColor} fontSize="sm">
                          {cert}
                        </Text>
                      </HStack>
                    </MotionBox>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </MotionBox>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default Skills;
